import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SlashCommandExtension } from './extension';
const INITIAL_SLASH_COMMAND_STATE = {
    isOpen: false,
    query: '',
    items: [],
    selectedIndex: 0,
    anchorEl: null,
};
const createInitialSlashCommandState = () => ({
    ...INITIAL_SLASH_COMMAND_STATE,
});
export const useSlashCommand = (options) => {
    const [state, setState] = useState(createInitialSlashCommandState);
    const stateRef = useRef(state);
    const suggestionRef = useRef(null);
    const helpersRef = useRef({});
    const additionalItemsRef = useRef(undefined);
    const enabled = options?.enabled ?? true;
    useEffect(() => {
        stateRef.current = state;
    }, [state]);
    useEffect(() => {
        helpersRef.current = options?.helpers ?? {};
    }, [options?.helpers]);
    useEffect(() => {
        additionalItemsRef.current = options?.additionalItems;
    }, [options?.additionalItems]);
    const resetState = useCallback(() => {
        suggestionRef.current = null;
        setState(() => createInitialSlashCommandState());
    }, []);
    const close = useCallback(() => {
        resetState();
    }, [resetState]);
    const setSelectedIndex = useCallback((nextIndex) => {
        setState((prev) => {
            if (!prev.isOpen || prev.items.length === 0) {
                return prev;
            }
            const clamped = Math.max(0, Math.min(nextIndex, prev.items.length - 1));
            if (clamped === prev.selectedIndex) {
                return prev;
            }
            return {
                ...prev,
                selectedIndex: clamped,
            };
        });
    }, []);
    const selectItem = useCallback((index) => {
        const suggestion = suggestionRef.current;
        if (!suggestion) {
            return;
        }
        const item = suggestion.items[index];
        if (!item) {
            return;
        }
        suggestion.command(item);
    }, []);
    const handleUpdate = useCallback((nextState) => {
        suggestionRef.current = nextState;
        setState((prev) => {
            const anchorEl = nextState.clientRect?.() ?? prev.anchorEl ?? null;
            const hasItems = nextState.items.length > 0;
            const shouldResetSelection = !prev.isOpen || prev.query !== nextState.query || !hasItems;
            let selectedIndex;
            if (hasItems) {
                if (shouldResetSelection) {
                    selectedIndex = 0;
                }
                else {
                    selectedIndex = Math.min(prev.selectedIndex, nextState.items.length - 1);
                }
            }
            else {
                selectedIndex = 0;
            }
            return {
                isOpen: true,
                query: nextState.query,
                items: nextState.items,
                selectedIndex,
                anchorEl,
            };
        });
    }, []);
    const handleExit = useCallback(() => {
        close();
    }, [close]);
    const handleKeyDown = useCallback(({ event }) => {
        const currentState = stateRef.current;
        if (!currentState.isOpen) {
            return false;
        }
        switch (event.key) {
            case 'ArrowDown': {
                event.preventDefault();
                const { items, selectedIndex } = currentState;
                if (items.length === 0) {
                    return true;
                }
                const nextIndex = (selectedIndex + 1) % items.length;
                setSelectedIndex(nextIndex);
                return true;
            }
            case 'ArrowUp': {
                event.preventDefault();
                const { items, selectedIndex } = currentState;
                if (items.length === 0) {
                    return true;
                }
                const nextIndex = (selectedIndex - 1 + items.length) % items.length;
                setSelectedIndex(nextIndex);
                return true;
            }
            case 'Enter':
            case 'Tab': {
                event.preventDefault();
                selectItem(currentState.selectedIndex);
                return true;
            }
            case 'Escape': {
                event.preventDefault();
                close();
                return true;
            }
            default:
                return false;
        }
    }, [close, selectItem, setSelectedIndex]);
    const extension = useMemo(() => {
        if (!enabled) {
            return null;
        }
        return SlashCommandExtension.configure({
            getHelpers: () => helpersRef.current,
            getAdditionalItems: () => additionalItemsRef.current,
            onUpdate: handleUpdate,
            onExit: handleExit,
            onKeyDown: handleKeyDown,
        });
    }, [enabled, handleUpdate, handleExit, handleKeyDown]);
    const controller = useMemo(() => ({
        state,
        setSelectedIndex,
        selectItem,
        close,
    }), [state, setSelectedIndex, selectItem, close]);
    return { extension, controller };
};
